<template>
  <div
    ref="footerRef"
    class="fixed bottom-[--raw-edge-space] z-10 flex justify-center gap-x-12 max-md:inset-x-0 md:left-[--raw-edge-space]"
  >
    <a
      :href="i18n(translations.legalUrl)"
      rel="noopener"
      @click="trackClick({ zone: 'footer', name: 'legal' })"
    >
      {{ i18n(translations.legalLabel) }}
    </a>

    <button
      v-for="doc in FOOTER_DOCS"
      :key="doc.id"
      type="button"
      @click="handleDoc(doc.id)"
    >
      {{ i18n(doc.label) }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import { useElementHeightVar } from '~/scopes/campaign/composables/useElementHeightVar'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import {
  FOOTER_DOCS,
  type FooterDocId,
} from '~/scopes/campaign/data/footer-docs'

import translations from './PageFooterLinks.translations'

const i18n = useI18n()
const { trackClick } = useTracking()
const { openDoc } = useModalState()

// Publish the row's live height so StatePanel rests above it (same CSS-var
// pattern as the header/zoom control; see --raw-footer-height in layout.css).
const footerRef = ref<HTMLElement | null>(null)
useElementHeightVar(footerRef, '--raw-footer-height')

// Two kinds of entry, hence two elements rather than one loop over a mixed list: Legal
// Mentions leaves the campaign (an `<a>`), the three documents open in place (`<button>`s,
// so assistive tech isn't told they navigate). The doc id doubles as the tracking label.
function handleDoc(id: FooterDocId) {
  trackClick({ zone: 'footer', name: id })
  openDoc(id)
}
</script>

<style scoped>
/* One appearance for both element types — the row reads as a single list of links, and a
   button is only how the three documents avoid claiming to navigate. `cursor: pointer` is
   on the button alone: the DS reset doesn't set it, where an `<a href>` gets it natively. */
a,
button {
  font-size: 10px;
  font-weight: 550;
  line-height: 1;

  @media screen(md) {
    font-size: 14px;
  }

  opacity: 70%;
  transition: opacity var(--raw-out-quart);

  @media (hover: hover) {
    &:hover {
      opacity: 100%;
    }
  }
}

button {
  cursor: pointer;
}
</style>
